import { Button, CircularProgress, IconButton, TextField } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faPlus, faCubes, faChevronRight, faChevronDown, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAiAssistant } from '../../AiAssistantContext';
import * as api from '../../api';
import { IProject } from '../../types';
import ProjectSettingsModal from './ProjectSettingsModal';

interface ProjectsPanelProps {
  collapsed: boolean;
  onToggle: () => void;
}

const ProjectsPanel: React.FC<ProjectsPanelProps> = ({ collapsed, onToggle }) => {
  const { state, dispatch } = useAiAssistant();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [settingsProject, setSettingsProject] = useState<IProject | null>(null);
  const [showFilter, setShowFilter] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);

  const loadProjects = useCallback(async () => {
    dispatch({ type: 'SET_LOADING_PROJECTS', loading: true });
    try {
      const projects = await api.fetchProjects();
      dispatch({ type: 'SET_PROJECTS', projects });
    } catch {
      dispatch({ type: 'SET_LOADING_PROJECTS', loading: false });
      setError('Failed to load projects');
    }
  }, [dispatch]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await api.createProject({ name: newName.trim() });
      setNewName('');
      setShowCreate(false);
      await loadProjects();
    } catch {
      setError('Failed to create project');
    }
    setCreating(false);
  };

  const handleSelect = (projectId: number) => {
    dispatch({ type: 'SELECT_PROJECT', projectId });
  };

  return (
    <div>
      <SectionHeader onClick={onToggle}>
        <HeaderLeft>
          <ChevronIcon icon={collapsed ? faChevronRight : faChevronDown} />
          <SectionTitle>Projects</SectionTitle>
        </HeaderLeft>
        <HeaderActions>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowFilter(!showFilter); if (showFilter) setFilterText(''); else setTimeout(() => filterInputRef.current?.focus(), 0); }} title="Filter">
            <FontAwesomeIcon icon={faSearch} size="xs" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setShowCreate(!showCreate); }}>
            <FontAwesomeIcon icon={faPlus} size="xs" />
          </IconButton>
        </HeaderActions>
      </SectionHeader>

      {!collapsed && showFilter && (
        <FilterRow>
          <FilterInput
            ref={filterInputRef}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter projects..."
            onKeyDown={(e) => { if (e.key === 'Escape') { setShowFilter(false); setFilterText(''); } }}
          />
          {filterText && (
            <IconButton size="small" onClick={() => setFilterText('')}>
              <FontAwesomeIcon icon={faTimes} size="xs" />
            </IconButton>
          )}
        </FilterRow>
      )}

      {!collapsed && (
        <>
          {error && <ErrorText>{error}</ErrorText>}

          {showCreate && (
            <CreateRow>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Project name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                fullWidth
                disabled={creating}
                inputProps={{ style: { padding: '6px 8px', fontSize: 13 } }}
              />
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleCreate}
                disabled={creating}
                style={{ marginLeft: 4 }}
              >
                {creating ? <CircularProgress size={14} color="inherit" /> : 'Add'}
              </Button>
            </CreateRow>
          )}

          {state.loadingProjects && (
            <LoadingRow>
              <CircularProgress size={16} />
            </LoadingRow>
          )}

          {!state.loadingProjects && state.projects.length === 0 && (
            <EmptyText>No projects yet. <EmptyAction onClick={() => setShowCreate(true)}>Create one</EmptyAction></EmptyText>
          )}

          {[...state.projects].filter((p) => { if (!filterText) return true; const name = p.name.toLowerCase(); return filterText.toLowerCase().split(/\s+/).filter(Boolean).every((w) => name.includes(w)); }).sort((a, b) => {
            const aFav = (state.favoriteSessionsByProject[a.id]?.length ?? 0) > 0 ? 0 : 1;
            const bFav = (state.favoriteSessionsByProject[b.id]?.length ?? 0) > 0 ? 0 : 1;
            return aFav - bFav;
          }).map((project) => {
            const hasFavs = (state.favoriteSessionsByProject[project.id]?.length ?? 0) > 0;
            return (
            <ProjectRow
              key={project.id}
              $selected={project.id === state.selectedProjectId}
              onClick={() => handleSelect(project.id)}
            >
              <ProjectIcon icon={faCubes} $hasFavs={hasFavs} />
              <ProjectName title={project.name}>{project.name}</ProjectName>
              <ProjectActions>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSettingsProject(project);
                  }}
                >
                  <FontAwesomeIcon icon={faCog} size="xs" />
                </IconButton>
              </ProjectActions>
            </ProjectRow>
            );
          })}
        </>
      )}

      {settingsProject && (
        <ProjectSettingsModal
          project={settingsProject}
          hasSessions={state.sessions.some((s) => s.projectId === settingsProject.id)}
          onClose={() => setSettingsProject(null)}
          onSave={(updated) => {
            dispatch({ type: 'UPDATE_PROJECT', project: updated });
            setSettingsProject(null);
          }}
          onDelete={(projectId) => {
            dispatch({ type: 'DELETE_PROJECT', projectId });
            setSettingsProject(null);
          }}
        />
      )}
    </div>
  );
};

export default ProjectsPanel;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  min-height: 30px;
  padding: 2px 2px 2px 8px;
  background: #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: #e0e0e0;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  font-size: 10px;
  color: #555;
  width: 10px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  ${SectionHeader}:hover & {
    opacity: 1;
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 4px 4px;
`;

const FilterInput = styled.input`
  font-size: 13px;
  border: 1px solid #bbb;
  border-radius: 3px;
  padding: 3px 6px;
  outline: none;
  flex: 1;
  min-width: 0;
  &:focus {
    border-color: #1976d2;
  }
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  color: #555;
  letter-spacing: 0.5px;
`;

const CreateRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 6px;
`;

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0;
`;

const EmptyText = styled.div`
  font-size: 12px;
  color: #999;
  padding: 8px 0;
`;

const EmptyAction = styled.span`
  color: #1976d2;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.div`
  font-size: 11px;
  color: #e53935;
  padding: 2px 0 4px;
`;

const ProjectRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  background: ${(p) => (p.$selected ? '#e3f2fd' : 'transparent')};
  &:hover {
    background: ${(p) => (p.$selected ? '#e3f2fd' : '#f0f0f0')};
  }
`;

const ProjectIcon = styled(FontAwesomeIcon)<{ $hasFavs?: boolean }>`
  font-size: 11px;
  color: ${(p) => (p.$hasFavs ? '#ffc107' : '#90a4ae')};
  margin-right: 6px;
  flex-shrink: 0;
`;

const ProjectName = styled.div`
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const ProjectActions = styled.div`
  display: flex;
  opacity: 0;
  ${ProjectRow}:hover & {
    opacity: 1;
  }
`;
